import parse from 'html-react-parser';
import _ from 'lodash';
import history from 'providers/history';
import React, { ReactNode } from 'react';
import Router from 'react-router';

import { BrandInfoModels, BrandInfoPager } from 'types/drupal/BrandInfo';
import { InventoryInstrument } from 'types/drupal/InstrumentNode';

import BreadcrumbTrail from 'components/global/application/BreadcrumbTrail';
import BrandInfoModelLinks from 'components/pages/brand/BrandInfoModelLinks';
import BrandInfoModelLinksMobile from 'components/pages/brand/BrandInfoModelLinksMobile';
import BrandPager from 'containers/pages/brand/BrandInfoPager';

import InventoryItem from 'components/pages/inventory/InventoryItem';

import FontAwesome from 'react-fontawesome';

import FailsafeImg from 'components/global/application/FailsafeImg';
import { imageUrlAppendStyle } from 'contexts/link_util';

import './BrandInfoItem.sass';

import BrandInfoYearSelect from 'components/pages/brand/BrandInfoYearSelect';

import { Helmet } from 'react-helmet';

interface OwnProps {
    match: Router.match<{brand: string, year?: string, model?: string}>;
}

export interface StateProps {
    brandContent: string;
    brandDefaultYear: string;
    brandImageUrl: string;
    brandInstruments: InventoryInstrument[];
    brandMetaTitle: string;
    brandMetaDesc: string;
    brandModels: BrandInfoModels[];
    brandName: string;
    brandModel: string;
    brandPager: BrandInfoPager;
    brandTitle: string;
    fetchFailed: boolean;
    fetching: boolean;
    isMobile: boolean;
}

export interface DispatchProps {
    loadBrandInstrumentsInfo(brandName: string, brandYear: string, page: string): void;
    loadBrandInstrumentsWithModelInfo(brandName: string, brandModel: string, brandYear: string, page: string): void;
    loadBrandModelsInfo(brandName: string, brandYear: string): void;
    loadBrandInfo(brandName: string): void;
    setPerPage(perPage: string): void;
}

type CombinedProps = OwnProps & StateProps & DispatchProps;

class BrandInfo extends React.Component<CombinedProps, {}> {

    constructor(props: CombinedProps) {
        super(props);
        this.updateQueryString = this.updateQueryString.bind(this);
    }

    public componentDidMount () {
        this.props.loadBrandInfo(this.props.match.params.brand);
    }

    public componentDidUpdate(prevProps: CombinedProps) {
        if (this.props.fetchFailed) {
            history.push('/');
            return;
        }

        if (this.props.brandName !== prevProps.brandName) {
            if (!this.props.match.params.year) {
                return this.redirectToCorrectDefaultYear();
            }
            return this.loadModelsAndInstruments();
        }

        if (this.props.match.params.year !== prevProps.match.params.year) {
            return this.loadModelsAndInstruments();
        }

        if (this.props.match.params.model !== prevProps.match.params.model) {
            this.props.loadBrandInstrumentsWithModelInfo(this.props.match.params.brand, this.props.match.params.model, this.props.match.params.year, String(this.props.brandPager.currentPage));
            return;
        }

        return;
    }

    public redirectToCorrectDefaultYear() {
        history.push('/brands/' + this.props.match.params.brand + '/' + this.props.brandDefaultYear + '/' + this.props.brandModel);
        return;
    }

    public loadModelsAndInstruments() {
        this.props.loadBrandModelsInfo(this.props.match.params.brand, this.props.match.params.year);
        this.props.loadBrandInstrumentsWithModelInfo(this.props.match.params.brand, this.props.match.params.model, this.props.match.params.year, String(this.props.brandPager.currentPage));
        return;
    }

    public stringToProperCase = (input: string) => {
        return input.toLowerCase().replace(/^(.)|\s(.)/g, $1 =>  $1.toUpperCase());
    }

    public updateQueryString = (key: string, value?: string) => {
        if (key === 'results_per_page') {
            this.props.setPerPage(value);
            return this.props.loadBrandInstrumentsWithModelInfo(this.props.match.params.brand, this.props.match.params.model, this.props.match.params.year, String(this.props.brandPager.currentPage));
        }

        this.props.loadBrandInstrumentsWithModelInfo(this.props.match.params.brand, this.props.match.params.model, this.props.match.params.year, value);
    }

    public breadcrumbTrail = () => {

        const yearCoalesce = this.props.match.params.year || '';

        const breadCrumbTrail =  [
            {
                label: 'Home',
                route: '/'
            },
            {
                label: 'Brands',
                route: '/brands/'
            },
            {
                label: this.stringToProperCase(this.props.match.params.brand),
                route: '/brands/' + this.props.match.params.brand + '/' + yearCoalesce
            }
        ];

        if (this.props.brandModel) {
            breadCrumbTrail.push({
                label: this.stringToProperCase(this.props.brandModel),
                route: '/brands/' + this.props.match.params.brand + '/' + this.props.match.params.year + '/' + this.props.brandModel
            });
        }

        return breadCrumbTrail;
    }

    public resultsSpinner() {
        if (this.props.fetching) {
            return (<FontAwesome className="loading-spinner" name="spinner" size="3x" spin />);
        }

        if (this.props.brandInstruments && this.props.brandInstruments.length <= 0) {
            return (<div className="w-100 text-center">No Results</div>);
        }

        return;
    }

    public render() {
        const content =  this.props.isMobile ? this.renderMobile() : this.renderDesktop();

        return(
             <div>
                {this.helmet()}
                {content}
            </div>
        );
    }

    public helmet() {
        return (
            <Helmet>
                <meta name="title" content={this.props.brandMetaTitle}/>
                <meta name="description" content={this.props.brandMetaDesc} />
                <title>{this.props.brandMetaTitle}</title>
            </Helmet>
        );
    }

    public renderDesktop() {
        return(
            <div className="Inventory-container brandContainer row">
                <div className="Side-pane col-4">
                    <div className="d-flex">
                        <h5 className="mt-3">Models</h5>
                        <BrandInfoYearSelect
                            className="mt-3 ml-3 w-100 "
                            brandName={this.props.brandName}
                            brandModel={this.props.brandModel}
                            year={this.props.match.params.year}
                        />
                    </div>
                    <BrandInfoModelLinks
                            brand={this.props.brandName}
                            brandModels={this.props.brandModels}
                            year={this.props.match.params.year}
                    />
                </div>
                <div className="col-8 Results-area">
                    <div className="Results-breadcrumb"><BreadcrumbTrail pathNodes={this.breadcrumbTrail()} /></div>
                    <div className="brandTitle"><h2>{this.props.brandTitle}</h2></div>
                    <div className="brandContent">
                        <FailsafeImg className="brandImage" src={imageUrlAppendStyle(this.props.brandImageUrl, 'medium_fill')} />
                        {parse(this.props.brandContent)}
                    </div>
                    <div className="row">
                        {this.resultsSpinner()}
                        { this.props.brandInstruments.map((instrument) => {
                            return (
                                <div key={instrument.nid} className="col-3">
                                    <InventoryItem
                                        isMobile={this.props.isMobile}
                                        item={instrument}
                                    />
                                </div>
                            );
                        })
                        }
                    </div>
                    <BrandPager updateQueryString={this.updateQueryString} />
                </div>
            </div>
        );
    }

    public renderMobile() {
        return(
            <div className="Inventory-container-mobile brandContainer row">
                <div className="col-12 Results-area">
                    <div className="Results-breadcrumb"><BreadcrumbTrail pathNodes={this.breadcrumbTrail()} /></div>
                    <div className="brandTitle"><h2>{this.props.brandTitle}</h2></div>
                    <div className="content" dangerouslySetInnerHTML={{ __html: this.props.brandContent }} />
                    <div className="row">
                        <div className="col-10 offset-1">
                            <BrandInfoYearSelect
                                className="col-10 offset-1"
                                brandName={this.props.brandName}
                                brandModel={this.props.brandModel}
                                year={this.props.match.params.year}
                            />
                            <BrandInfoModelLinksMobile
                                brand={this.props.brandName}
                                brandModels={this.props.brandModels}
                                year={this.props.match.params.year}
                            />
                        </div>
                        <div className="col-10 offset-1 ResultsTitle">&nbsp;</div>
                        {this.resultsSpinner()}
                        { this.props.brandInstruments.map((instrument) => {
                            return (
                                <div key={instrument.nid} className="col-12">
                                    <InventoryItem
                                        isMobile={this.props.isMobile}
                                        item={instrument}
                                    />
                                </div>
                            );
                        })
                        }
                    </div>
                    <BrandPager updateQueryString={this.updateQueryString} />
                </div>
            </div>
        );
    }
}

export default BrandInfo;



// WEBPACK FOOTER //
// ./src/components/pages/brand/BrandInfo.tsx